import type { KpiMode, KpiSignal } from "./types";
import { computeScheduleKpiSignal, type ProgressPoint } from "./schedule";
import {
  computeMissionKpiSignal,
  type MissionMetricPoint,
  type MissionConfig,
  type TargetDef,
} from "./mission";
// DEV-851 (PR2): Legacy `computeReliabilityKpiSignal` retired. App.tsx
// now drives data reliability scoring through `computeDataReliability`
// in src/kpi-engine/dataReliability/. The legacy reliability.ts file
// is dead code from this engine's perspective — leaving it on disk
// for one cleanup pass to confirm no other callers, then removing.
import {
  computeSummaryKpiSignal,
  type SummaryConfig,
} from "./summary";

export type KpiConfig = {
  schedule: {
    yellow_threshold_pp: number;
    red_threshold_pp: number;
    score_zero_at_behind_pp?: number;
    trend_epsilon_pp?: number;
  };
  mission?: MissionConfig;
  data_reliability?: {
    default_permissible_days_late?: number;
    decay_days: number;
  };
  summary: SummaryConfig;
};

export type OfficialReportingCalendar = Record<
  string,
  { last_closed_official_date: string }
>;

export type KpiBundle = {
  as_of_date: string;
  schedule: KpiSignal;
  mission: KpiSignal;
  /**
   * DEV-851 (PR2): retained as null pass-through for callers that still
   * read `bundle.data_reliability` while migration finishes. App.tsx
   * sources the live score / color / trend / now / outlook from the
   * `computeDataReliability` engine output instead. Once consumers have
   * fully migrated, this field can be removed.
   */
  data_reliability: KpiSignal;
  summary: KpiSignal;
  mission_subkpis: any[];
};

export function resolveAsOfDate(
  mode: KpiMode,
  projectGuid: string,
  calendar: OfficialReportingCalendar | null | undefined,
): string {
  if (
    mode === "official" &&
    calendar?.[projectGuid]?.last_closed_official_date
  ) {
    return calendar[projectGuid].last_closed_official_date;
  }
  // Live mode: today (ISO date).
  return new Date().toISOString().slice(0, 10);
}

export function computeKpisForProject(args: {
  projectGuid: string;
  mode: KpiMode;
  asOfDate: string;
  config: KpiConfig;
  schedulePoints: ProgressPoint[];
  missionPoints: MissionMetricPoint[];
  /** Optional per-project mission targets. If omitted, engine falls back to seed data. */
  missionTargets?: Record<string, TargetDef>;
}): KpiBundle {
  const {
    asOfDate,
    config,
    schedulePoints,
    missionPoints,
    missionTargets: projectTargets,
  } = args;

  const schedule = computeScheduleKpiSignal(
    schedulePoints,
    asOfDate,
    config.schedule,
  );

  const missionCfg: MissionConfig = config.mission ?? { subkpis: {} };
  const { signal: mission, subkpis: mission_subkpis } = computeMissionKpiSignal(
    missionPoints,
    asOfDate,
    missionCfg,
    projectTargets,
  );

  // DEV-851 (PR2): Legacy data-reliability scoring removed from this path.
  // App.tsx invokes `computeDataReliability` in src/kpi-engine/dataReliability/
  // and recomputes Summary using its `summary_score`. The placeholder below
  // preserves the bundle field shape until consumers fully migrate off it.
  const data_reliability: KpiSignal = {
    score_0_100: null,
    indicator_color_value: 0,
    indicator_trend_value: 0,
    as_of_date: asOfDate,
    explain: { headline: "Reliability score is sourced from computeDataReliability in App.tsx." },
  };

  // Finance: score is null here — real finance scoring happens in App.tsx via
  // computeFinanceCategoryFromData(). Summary auto-excludes null-scored categories.
  // Data reliability also null here — App.tsx recomputes Summary with the
  // engine v2 summary_score (DEV-851).
  const summary = computeSummaryKpiSignal(asOfDate, config.summary, {
    schedule: {
      score: schedule.score_0_100,
      trend: schedule.indicator_trend_value,
    },
    finance: { score: null, trend: 0 },
    mission: {
      score: mission.score_0_100,
      trend: mission.indicator_trend_value,
    },
    data_reliability: { score: null, trend: 0 },
  });

  return {
    as_of_date: asOfDate,
    schedule,
    mission,
    data_reliability,
    summary,
    mission_subkpis,
  };
}
